import { Eye, EyeOff, IndianRupee, Pencil, Plus, Trash2 } from 'lucide-react';
import * as React from 'react';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, Combobox, Dialog,
  DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
  Switch,
  Tabs, TabsContent, TabsList, TabsTrigger, Tooltip, TooltipContent, TooltipTrigger,
  Textarea,
} from '@/components/cargofl-ui';
import { HolidaysPanel } from '@/components/admin/holidays-panel';
import { RatesDialog, type RateKind } from '@/components/admin/rates-dialog';
import { money } from '@/lib/invoice-api';
import { RevenuePanel } from '@/components/admin/revenue-panel';
import { TagsPanel } from '@/components/admin/tags-panel';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { ApiError, toastFailure } from '@/lib/api';
import { TeamPicker } from '@/components/admin/team-picker';
import { ConfirmButton } from '@/components/confirm-dialog';
import {
  useCreateActivity, useCreateCustomer, useCreateProject, useDeleteCatalogItem,
  useUpdateActivity, useUpdateCustomer, useUpdateProject,
} from '@/lib/admin-api';
import { useActivities, useCustomers, useProjects } from '@/lib/catalog-api';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * What time can be booked against, and the holiday calendar.
 *
 * Retiring is the normal operation here, not deleting: every one of these rows is
 * referenced by time entries that approved months depend on. Hiding stops it being
 * offered for new bookings while every historic figure still adds up, and the API
 * refuses a delete that would take hours with it.
 */
const TABS = ['customers', 'projects', 'activities', 'tags', 'holidays', 'revenue'] as const;

export function AdminSetupPage() {
  // The tab lives in the PATH, not in component state, so each one can be linked
  // to and bookmarked - the sidebar's "Public holidays" entry needs somewhere to
  // point, and an unrecognised tab falls back rather than rendering nothing.
  const { tab } = useParams();
  const navigate = useNavigate();
  const active = TABS.includes(tab as (typeof TABS)[number]) ? tab! : 'customers';

  return (
    <PageContainer>
      <PageHeader
        title="Setup"
        description="Customers, projects, activities, tags, holidays and customer revenue."
      />
      <Tabs
        value={active}
        onValueChange={(next) => navigate(`/admin/setup/${next}`, { replace: true })}
      >
        <TabsList>
          <TabsTrigger value="customers">Customers</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="activities">Activities</TabsTrigger>
          <TabsTrigger value="tags">Tags</TabsTrigger>
          <TabsTrigger value="holidays">Holidays</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
        </TabsList>
        <TabsContent value="customers"><Customers /></TabsContent>
        <TabsContent value="projects"><Projects /></TabsContent>
        <TabsContent value="activities"><Activities /></TabsContent>
        <TabsContent value="tags"><TagsPanel /></TabsContent>
        <TabsContent value="holidays"><HolidaysPanel /></TabsContent>
        <TabsContent value="revenue"><RevenuePanel /></TabsContent>
      </Tabs>
    </PageContainer>
  );
}

/** Shared chrome: a count, an add button, an error slot and a scrolling table. */
function Panel({
  count,
  onAdd,
  addLabel,
  error,
  children,
}: {
  count?: number;
  onAdd: () => void;
  addLabel: string;
  error: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="ts-num text-sm text-muted-foreground">
          {count === undefined ? '' : `${count} shown`}
        </span>
        <Button onClick={onAdd}>
          <Plus className="size-4" />
          {addLabel}
        </Button>
      </div>
      {error && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
        </Alert>
      )}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">{children}</div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Hide / show and delete, side by side.
 *
 * Both are offered because both are legitimate: a project that ran its course is
 * hidden, one created by mistake is deleted. The API decides which is allowed and
 * refuses the delete with the entry count when hours are booked against it, so the
 * button is not pre-emptively disabled - the refusal explains itself better than a
 * greyed-out control does.
 */
function RowActions({
  visible,
  itemLabel,
  onEdit,
  onToggle,
  onDelete,
  onRates,
  busy,
}: {
  visible: boolean;
  /** Named in the delete confirmation, e.g. `the project "Wirtgen"`. */
  itemLabel: string;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
  /** Omitted for anyone who may not see money - see canViewRates in shared. */
  onRates?: () => void;
  busy: boolean;
}) {
  return (
    <div className="flex justify-end gap-1">
      {onRates && (
        <Button variant="ghost" size="icon" aria-label="Rates" title="Rates" onClick={onRates}>
          <IndianRupee className="size-4" />
        </Button>
      )}
      <Button variant="ghost" size="icon" aria-label="Edit" onClick={onEdit}>
        <Pencil className="size-4" />
      </Button>
      {/*
        Hiding asks; showing does not.
        
        I originally skipped the confirmation here on the grounds that clicking
        again undoes it. That was wrong, and somebody proved it by mis-clicking
        this icon: a retired row is still listed on this screen, but the icon is a
        single ghost button among three, and "retire this customer" is not what
        anybody expects a stray click to do. Showing something again needs no
        confirmation - it grants nothing and is undone by the same click.
      */}
      {visible ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <ConfirmButton
              variant="ghost"
              size="icon" hint={false}
              aria-label="Hide"
              disabled={busy}
              pending={busy}
              title={`Retire ${itemLabel}?`}
              description={
                <>
                  It stops appearing in the booking pickers, so nobody can record new
                  time against it. Everything already recorded is untouched and still
                  counts in the reports. It stays on this screen, marked as retired, and
                  you can bring it back at any time.
                </>
              }
              confirmLabel="Retire it"
              onConfirm={onToggle}
            >
              <EyeOff className="size-4" />
            </ConfirmButton>
          </TooltipTrigger>
          <TooltipContent>Retire it — history is kept, new bookings stop</TooltipContent>
        </Tooltip>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" hint={false} aria-label="Show" disabled={busy} onClick={onToggle}>
              <Eye className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Make it bookable again</TooltipContent>
        </Tooltip>
      )}
      {/* Delete asks; Hide does not. Hiding is undone by clicking again, and a
          confirm on everything is a confirm nobody reads. */}
      <ConfirmButton
        variant="ghost"
        size="icon"
        aria-label="Delete"
        disabled={busy}
        pending={busy}
        title={`Delete ${itemLabel}?`}
        description={
          <>
            This is refused outright if any hours are booked against it, and the message
            will say how many &mdash; hide it instead in that case, which keeps the history
            and stops new bookings. Deleting is only possible while nothing refers to it.
          </>
        }
        confirmLabel="Delete"
        destructive
        onConfirm={onDelete}
      >
        <Trash2 className="size-4" />
      </ConfirmButton>
    </div>
  );
}

function useCatalogActions() {
  const remove = useDeleteCatalogItem();
  const [error, setError] = React.useState<string | null>(null);
  // Named, and toasted as well as set: the slot is above a table of ~60 rows, so
  // a refused toggle near the bottom used to look like a click that did nothing.
  const fail = (name: string) => (e: unknown) => {
    setError(`"${name}": ${e instanceof ApiError ? e.message : 'that did not work.'}`);
    toastFailure(`Could not change "${name}"`)(e);
  };
  return { remove, error, setError, fail };
}

function Customers() {
  const { data, isPending } = useCustomers(true);
  const update = useUpdateCustomer();
  const { remove, error, setError, fail } = useCatalogActions();
  const [editing, setEditing] = React.useState<{ id?: string; name: string } | null>(null);
  const [rating, setRating] = React.useState<{ id: string; name: string; currency: string } | null>(null);

  if (isPending) return <Skeleton className="h-64 w-full" />;

  return (
    <>
      <Panel count={data?.length} addLabel="Add a customer" error={error}
        onAdd={() => setEditing({ name: '' })}>
        <Table className="min-w-[600px]">
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Number</TableHead>
              {/* The first of the three levels: an entry is billable only if its
                  customer, project AND activity all are. */}
              <TableHead>Billable</TableHead>
              <TableHead className="text-right">Projects</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data ?? []).map((c) => (
              <TableRow key={c.id} className={c.visible ? undefined : 'ts-locked'}>
                <TableCell className="font-medium">
                  <span className="flex items-center gap-2">
                    {c.color && (
                      <span
                        className="size-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: c.color }}
                        aria-hidden
                      />
                    )}
                    {c.name}
                    {!c.visible && <Badge variant="outline">hidden</Badge>}
                  </span>
                  {c.comment && (
                    <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
                      {c.comment}
                    </span>
                  )}
                </TableCell>
                <TableCell className="ts-num text-sm text-muted-foreground">
                  {c.number ?? '—'}
                </TableCell>
                <TableCell>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Switch
                      checked={c.billable}
                      onCheckedChange={(v: boolean) =>
                        update.mutate({ id: c.id, billable: v }, { onError: fail(c.name) })
                      }
                      aria-label={`Hours on ${c.name} are billable`}
                    />
                    {c.billable ? 'billable' : 'internal'}
                  </label>
                </TableCell>
                <TableCell className="ts-num text-right">{c._count.projects}</TableCell>
                <TableCell>
                  <RowActions
                    visible={c.visible}
                    itemLabel={`the customer "${c.name}"`}
                    busy={update.isPending || remove.isPending}
                    onRates={() => setRating({ id: c.id, name: c.name, currency: c.currency })}
                    onEdit={() => setEditing({ id: c.id, name: c.name })}
                    onToggle={() => {
                      setError(null);
                      update.mutate({ id: c.id, visible: !c.visible }, { onError: fail(c.name) });
                    }}
                    onDelete={() => {
                      setError(null);
                      remove.mutate({ kind: 'customers', id: c.id }, { onError: fail(c.name) });
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
      {editing && <CustomerDialog value={editing} onClose={() => setEditing(null)} />}
      {rating && (
        <RatesDialog
          kind="customers"
          id={rating.id}
          name={rating.name}
          currency={rating.currency}
          canEdit
          budget={data?.find((row) => row.id === rating.id)}
          onClose={() => setRating(null)}
        />
      )}
    </>
  );
}

function CustomerDialog({
  value,
  onClose,
}: {
  value: { id?: string; name: string };
  onClose: () => void;
}) {
  const create = useCreateCustomer();
  const update = useUpdateCustomer();
  const [name, setName] = React.useState(value.name);
  const [error, setError] = React.useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      if (value.id) await update.mutateAsync({ id: value.id, name });
      else await create.mutateAsync({ name });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that customer.');
    }
  };

  return (
    <SimpleDialog
      title={value.id ? 'Edit customer' : 'Add a customer'}
      onClose={onClose}
      onSubmit={submit}
      busy={create.isPending || update.isPending}
      error={error}
    >
      <div className="space-y-1.5">
        <Label htmlFor="customer-name">Name</Label>
        <Input id="customer-name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
    </SimpleDialog>
  );
}

function Projects() {
  const { data, isPending } = useProjects(true);
  const { data: customers } = useCustomers(true);
  const [rating, setRating] = React.useState<{ id: string; name: string } | null>(null);
  const update = useUpdateProject();
  const { remove, error, setError, fail } = useCatalogActions();
  const [editing, setEditing] = React.useState<
    | { id?: string; name: string; customerId: string; comment: string; color: string; teamIds: string[] }
    | null
  >(null);

  if (isPending) return <Skeleton className="h-64 w-full" />;

  return (
    <>
      <Panel count={data?.length} addLabel="Add a project" error={error}
        onAdd={() => setEditing({ name: '', customerId: customers?.[0]?.id ?? '', comment: '', color: '', teamIds: [] })}>
        <Table className="min-w-[680px]">
          <TableHeader>
            <TableRow>
              <TableHead>Project</TableHead>
              <TableHead>Customer</TableHead>
              {/* Utilisation is billable time over capacity, so this column is
                  the difference between that report meaning something and
                  counting Leave as client work. */}
              <TableHead>Billable</TableHead>
              <TableHead className="text-right">Activities</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data ?? []).map((p) => (
              <TableRow key={p.id} className={p.visible ? undefined : 'ts-locked'}>
                <TableCell className="font-medium">
                  <span className="flex items-center gap-2">
                    {/* Both of these were migrated and shown nowhere until now. */}
                    {p.color && (
                      <span
                        className="size-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: p.color }}
                        aria-hidden
                      />
                    )}
                    {p.name}
                    {!p.visible && <Badge variant="outline">hidden</Badge>}
                  </span>
                  {p.comment && (
                    <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
                      {p.comment}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {p.customer.name}
                  {/* Money already billed against the project outside the
                      timesheet. Absent for anyone who may not see rates - the API
                      strips the field rather than sending a zero. */}
                  {p.billedMinor && p.billedMinor !== '0' && (
                    <span className="ts-num block text-xs text-foreground">
                      {money(p.billedMinor, 'INR')} billed
                      {p.billings?.[0] ? ` (${p.billings[0].billedOn})` : ''}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Switch
                      checked={p.billable}
                      onCheckedChange={(v: boolean) =>
                        update.mutate({ id: p.id, billable: v }, { onError: fail(p.name) })
                      }
                      aria-label={`Hours on ${p.name} are billable`}
                    />
                    {p.billable ? 'billable' : 'internal'}
                  </label>
                </TableCell>
                <TableCell className="text-right">
                  <span className="ts-num">{p._count.activities}</span>
                  {/* Off on 5 migrated projects and enforced on every save; until
                      this switch nothing could change it. */}
                  <label className="mt-1 flex items-center justify-end gap-2 text-xs text-muted-foreground">
                    <Switch
                      checked={p.globalActivities}
                      onCheckedChange={(v: boolean) =>
                        update.mutate({ id: p.id, globalActivities: v }, { onError: fail(p.name) })
                      }
                      aria-label={`${p.name} accepts shared activities`}
                    />
                    {p.globalActivities ? '+ shared' : 'own only'}
                  </label>
                </TableCell>
                <TableCell>
                  <RowActions
                    visible={p.visible}
                    itemLabel={`the project "${p.name}"`}
                    busy={update.isPending || remove.isPending}
                    onRates={() => setRating({ id: p.id, name: p.name })}
                    onEdit={() =>
                        setEditing({
                          id: p.id,
                          name: p.name,
                          customerId: p.customer.id,
                          comment: p.comment ?? '',
                          color: p.color ?? '',
                          teamIds: p.teams.map((t) => t.id),
                        })
                      }
                    onToggle={() => {
                      setError(null);
                      update.mutate({ id: p.id, visible: !p.visible }, { onError: fail(p.name) });
                    }}
                    onDelete={() => {
                      setError(null);
                      remove.mutate({ kind: 'projects', id: p.id }, { onError: fail(p.name) });
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
      {editing && (
        <ProjectDialog
          value={editing}
          customers={customers ?? []}
          onClose={() => setEditing(null)}
        />
      )}
      {rating && (
        <RatesDialog
          kind="projects"
          id={rating.id}
          name={rating.name}
          currency="INR"
          canEdit
          budget={data?.find((row) => row.id === rating.id)}
          onClose={() => setRating(null)}
        />
      )}
    </>
  );
}

function ProjectDialog({
  value,
  customers,
  onClose,
}: {
  value: { id?: string; name: string; customerId: string; comment: string; color: string; teamIds: string[] };
  customers: { id: string; name: string }[];
  onClose: () => void;
}) {
  const create = useCreateProject();
  const update = useUpdateProject();
  const [name, setName] = React.useState(value.name);
  const [customerId, setCustomerId] = React.useState(value.customerId);
  const [comment, setComment] = React.useState(value.comment);
  const [color, setColor] = React.useState(value.color);
  const [teamIds, setTeamIds] = React.useState<string[]>(value.teamIds);
  const [error, setError] = React.useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!customerId) return setError('Choose a customer.');
    try {
      const fields = {
        name, customerId, comment: comment.trim() || null, color: color || null, teamIds,
      };
      if (value.id) await update.mutateAsync({ id: value.id, ...fields });
      else await create.mutateAsync(fields);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that project.');
    }
  };

  return (
    <SimpleDialog
      title={value.id ? 'Edit project' : 'Add a project'}
      onClose={onClose}
      onSubmit={submit}
      busy={create.isPending || update.isPending}
      error={error}
    >
      <div className="space-y-1.5">
        <Label htmlFor="project-name">Name</Label>
        <Input id="project-name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="space-y-1.5">
        <Label>Customer</Label>
        <Combobox
          value={customerId || null}
          onChange={setCustomerId}
          options={customers.map((c) => ({ value: c.id, label: c.name }))}
          placeholder="Choose a customer"
          searchPlaceholder="Search customers"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="project-comment">Note (optional)</Label>
        <Textarea
          id="project-comment"
          rows={2}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="project-color">Colour (optional)</Label>
        <div className="flex items-center gap-2">
          {/* The native colour input rather than a picker component: it is one
              element, it is accessible, and the browser already has one. */}
          <input
            id="project-color"
            type="color"
            value={color || '#008a5c'}
            onChange={(e) => setColor(e.target.value)}
            className="h-9 w-14 cursor-pointer rounded border bg-background"
          />
          {color && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setColor('')}>
              Clear
            </Button>
          )}
        </div>
      </div>
      <TeamPicker value={teamIds} onChange={setTeamIds} />
    </SimpleDialog>
  );
}

function Activities() {
  const { data, isPending } = useActivities(true);
  const [rating, setRating] = React.useState<{ id: string; name: string } | null>(null);
  const { data: projects } = useProjects(true);
  const update = useUpdateActivity();
  const { remove, error, setError, fail } = useCatalogActions();
  const [editing, setEditing] = React.useState<
    { id?: string; name: string; projectId: string | null } | null
  >(null);

  if (isPending) return <Skeleton className="h-64 w-full" />;

  return (
    <>
      <Panel count={data?.length} addLabel="Add an activity" error={error}
        onAdd={() => setEditing({ name: '', projectId: null })}>
        <Table className="min-w-[560px]">
          <TableHeader>
            <TableRow>
              <TableHead>Activity</TableHead>
              <TableHead>Scope</TableHead>
              {/* The third level. "Public Holiday" is the one that matters: the
                  nightly fill's own bookings must not count as client work. */}
              <TableHead>Billable</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data ?? []).map((a) => (
              <TableRow key={a.id} className={a.visible ? undefined : 'ts-locked'}>
                <TableCell className="font-medium">
                  <span className="flex items-center gap-2">
                    {a.color && (
                      <span
                        className="size-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: a.color }}
                        aria-hidden
                      />
                    )}
                    {a.name}
                    {a.number && (
                      <span className="ts-num text-xs font-normal text-muted-foreground">
                        {a.number}
                      </span>
                    )}
                    {!a.visible && <Badge variant="outline">hidden</Badge>}
                  </span>
                  {a.comment && (
                    <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
                      {a.comment}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {a.project ? (
                    <span className="text-sm text-muted-foreground">{a.project.name}</span>
                  ) : (
                    <Badge variant="outline">any project</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Switch
                      checked={a.billable}
                      onCheckedChange={(v: boolean) =>
                        update.mutate({ id: a.id, billable: v }, { onError: fail(a.name) })
                      }
                      aria-label={`Hours on ${a.name} are billable`}
                    />
                    {a.billable ? 'billable' : 'internal'}
                  </label>
                </TableCell>
                <TableCell>
                  <RowActions
                    visible={a.visible}
                    itemLabel={`the activity "${a.name}"`}
                    busy={update.isPending || remove.isPending}
                    onRates={() => setRating({ id: a.id, name: a.name })}
                    onEdit={() =>
                      setEditing({ id: a.id, name: a.name, projectId: a.project?.id ?? null })
                    }
                    onToggle={() => {
                      setError(null);
                      update.mutate({ id: a.id, visible: !a.visible }, { onError: fail(a.name) });
                    }}
                    onDelete={() => {
                      setError(null);
                      remove.mutate({ kind: 'activities', id: a.id }, { onError: fail(a.name) });
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
      {editing && (
        <ActivityDialog
          value={editing}
          projects={projects ?? []}
          onClose={() => setEditing(null)}
        />
      )}
      {rating && (
        <RatesDialog
          kind="activities"
          id={rating.id}
          name={rating.name}
          currency="INR"
          canEdit
          budget={data?.find((row) => row.id === rating.id)}
          onClose={() => setRating(null)}
        />
      )}
    </>
  );
}

function ActivityDialog({
  value,
  projects,
  onClose,
}: {
  value: { id?: string; name: string; projectId: string | null };
  projects: { id: string; name: string; customer: { name: string } }[];
  onClose: () => void;
}) {
  const create = useCreateActivity();
  const update = useUpdateActivity();
  const [name, setName] = React.useState(value.name);
  const [error, setError] = React.useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      if (value.id) await update.mutateAsync({ id: value.id, name });
      else await create.mutateAsync({ name, projectId: value.projectId });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that activity.');
    }
  };

  return (
    <SimpleDialog
      title={value.id ? 'Edit activity' : 'Add an activity'}
      onClose={onClose}
      onSubmit={submit}
      busy={create.isPending || update.isPending}
      error={error}
    >
      <div className="space-y-1.5">
        <Label htmlFor="activity-name">Name</Label>
        <Input id="activity-name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      {/* Scope is only settable on creation: the API refuses to move an activity that
          already has entries, because the pairing would no longer be one the reports
          can explain. */}
      {value.id ? (
        <p className="text-xs leading-relaxed text-muted-foreground">
          Which project an activity belongs to cannot be changed once hours are booked
          against it. Create a new activity instead.
        </p>
      ) : (
        <p className="text-xs leading-relaxed text-muted-foreground">
          New activities are bookable against any project. That is what almost all of
          the existing ones are.
        </p>
      )}
    </SimpleDialog>
  );
}

/** The shape every one of these small forms shares. */
function SimpleDialog({
  title,
  onClose,
  onSubmit,
  busy,
  error,
  children,
}: {
  title: string;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  busy: boolean;
  error: string | null;
  children: React.ReactNode;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          {children}
          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
